import { formatPrice } from '@repo/shared/utils/priceFormatter';
import { useSearch } from '@tanstack/react-router';
import { Fragment, useEffect, useRef } from 'react';
import { ContactDetail } from '@/components/contact-detail';
import {
	FarePolicies,
	FareSummary,
	getFareLineItems,
} from '@/components/fare-summary';
import { SectionHeader } from '@/components/section-header';
import { TripDetailsCard } from '@/components/trip-details-card';
import { SUCCESS_BOOKING_STATUS } from '@/routes/_auth/-components/constants';
import { useTripBookingData } from '../-store';
import { CustomerHelp } from './customer-help';
import { ModifyBooking } from './modify-booking';
import {
	RefundSummary,
	RefundTimeline,
	TransactionSummary,
} from './refund-info';

export function BookingDetailContent() {
	const bookingData = useTripBookingData();
	const hasScrolled = useRef(false);
	const { action } = useSearch({ from: '/_auth/trips/$bookingId/' });

	useEffect(() => {
		const scrollToSection =
			action === 'reschedule' ? 'modify-booking' : undefined;
		if (!scrollToSection || !bookingData || hasScrolled.current) return;
		hasScrolled.current = true;

		setTimeout(() => {
			const target = document.getElementById(scrollToSection);
			target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 300);
	}, [bookingData, action]);

	if (!bookingData) {
		return null;
	}
	const {
		farePolicies,
		cabDetail,
		extraPaymentInfo,
		refundDetail,
		rideDetails,
		contactDetails,
		cancellationDetail,
		rescheduleDetail,
		bookingDetail: { bookingStatus },
		parentBookingId,
	} = bookingData;
	const { pricing: cabPricing } = cabDetail || {};
	const { amountToBePaid, displayFares } = extraPaymentInfo || {};
	const { charges, discounts, fareAdjustmentLineItems } = getFareLineItems(
		cabPricing,
		true,
	);
	const isSuccessBooking = SUCCESS_BOOKING_STATUS.includes(bookingStatus);
	return (
		<Fragment>
			{refundDetail && (
				<div
					className="bg-primary p-20 flex flex-col gap-y-15"
					id="refund-status"
				>
					<SectionHeader title="Refund Status" />
					{refundDetail?.timeline && (
						<RefundTimeline timelineItems={refundDetail.timeline} />
					)}
					{refundDetail?.refundSummary && (
						<TransactionSummary refundSummary={refundDetail.refundSummary} />
					)}
				</div>
			)}
			{rideDetails && cabDetail && (
				<div className="bg-primary p-20 flex flex-col gap-y-15">
					<SectionHeader title="Booking Details" />
					<TripDetailsCard
						rideDetails={rideDetails}
						cabInfo={cabDetail}
						{...(bookingStatus === 'CANCELLED' && {
							mode: 'Collapsible',
							expanded: false,
						})}
					/>
				</div>
			)}
			{refundDetail?.breakUps && (
				<RefundSummary refundSummary={refundDetail.breakUps} />
			)}
			{cabPricing && (
				<FareSummary
					displayCharges={[...charges, ...(displayFares || [])]}
					discounts={discounts}
					fareAdjustmentLineItems={fareAdjustmentLineItems}
					paidCharges={
						cabPricing?.displayPrice !== undefined
							? [
									{
										label: 'Total Paid',
										value: formatPrice(cabPricing.displayPrice, {
											currency: cabPricing.currency,
										}),
									},
								]
							: []
					}
					toBePaidCharges={
						amountToBePaid
							? [
									{
										label: amountToBePaid?.label || '',
										value: amountToBePaid?.value || '',
									},
								]
							: []
					}
					mode="Collapsible"
				>
					{farePolicies && cabDetail && (
						<FarePolicies {...farePolicies} selectedCab={cabDetail} />
					)}
				</FareSummary>
			)}
			{contactDetails && (
				<ContactDetail
					mode="view"
					subtitle="We have sent the booking details on email & WhatsApp"
					contactDetails={contactDetails}
				/>
			)}
			{cabDetail && rideDetails && (
				<ModifyBooking
					id="modify-booking"
					cancellationDetail={cancellationDetail}
					rescheduleDetail={rescheduleDetail}
					cabDetail={cabDetail}
					rideDetails={rideDetails}
					parentBookingId={parentBookingId}
				/>
			)}
			{isSuccessBooking && (
				<div className="p-20 bg-primary">
					<CustomerHelp />
				</div>
			)}
		</Fragment>
	);
}
